import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Dropdown } from 'react-bootstrap';
import { getAdminCategory } from '../../../../Store/User/Category/AdminCategory';
import { PAY_URL } from '../../../../BaseURL';
import Header from '../../Pages/Header';
import Sidebar from '../../Pages/Sidebar';
import Loader from '../../Pages/Loader';
import { Pagination, Search } from '../../Table';
import CopyRight from '../../Pages/CopyRight';

const CategoryList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Redux state
  const { categories = [], loading } = useSelector(state => state.getAdminCategory || {});

  // Fetch categories on mount
  useEffect(() => {
    if (!localStorage.getItem('PAY_USER_TOKEN')) {
      navigate("/user/login");
    } else {
      dispatch(getAdminCategory());
    }
  }, [dispatch, navigate]);

  // Filtered + paginated data
  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    const filtered = search
      ? categories.filter(c => c?.name?.toLowerCase().includes(search.toLowerCase()))
      : categories;
    return filtered.slice((currentPage - 1) * limit, (currentPage - 1) * limit + limit);
  }, [categories, search, currentPage, limit]);

  const totalItems = Array.isArray(categories)
    ? (search
        ? categories.filter(c => c?.name?.toLowerCase().includes(search.toLowerCase())).length
        : categories.length)
    : 0;

  // Delete category
  const handleDelete = (id, title) => {
    Swal.fire({
      title: `Are you sure want to delete ${title}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${PAY_URL}/user/delete-category`, {
          data: { id },
          headers: { Authorization: "Bearer " + localStorage.getItem('PAY_USER_TOKEN') }
        }).then(res => {
          if (res.data.success) {
            toast.success(res.data.message);
            dispatch(getAdminCategory()); // Refresh list
          } else {
            toast.error(res.data.message);
          }
        }).catch(err => toast.error(err?.response?.data?.message || "Something went wrong"));
      }
    });
  };

  const handleRefresh = () => dispatch(getAdminCategory());
  const handleOpen = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className={`main-wrapper ${isSidebarOpen ? 'slide-nav' : ''}`}>
      <Header handleOpen={handleOpen} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title user-title">Category Management</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/user/dashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item active">Category Management</li>
                </ul>
              </div>
              <div className="col-auto float-end ms-auto">
                <Link to='/user/manage-category' className="btn btn-success">
                  <i className="fa-solid fa-plus"></i> Add Category
                </Link>
              </div>
            </div>
          </div>

          {/* Filter & Pagination Controls */}
          <div className="row filter-row justify-content-end">
            <div className="col-sm-12 col-md-8">
              <div className="row justify-content-end">
                <div className="col-md-12 mb-2">
                  <div className="d-flex flex-wrap justify-content-end gap-2 align-items-center">
                    <div className="d-flex gap-2 align-items-center">
                      <p className="mb-0">Rows per page:</p>
                      <Dropdown>
                        <Dropdown.Toggle variant="none" className='form-control selectWidth'>
                          {limit}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {[10, 20, 30, 50].map(l => l !== limit && (
                            <Dropdown.Item key={l} onClick={() => setLimit(l)}>{l}</Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <button className="btn btn-refresh" type="button" onClick={handleRefresh}>
                        <i className="fa-solid fa-arrows-rotate"></i>
                      </button>
                    </div>
                    <div className="tableSearch">
                      <Search onSearch={(value) => { setSearch(value); setCurrentPage(1); }} />
                      <span className="search"><i className="fa-solid fa-magnifying-glass"></i></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? <Loader /> : (
            <div className="card p-3">
              <div className="table-responsive">
                <table className="table table-striped custom-table mb-0">
                  <thead>
                    <tr>
                      <th>Sr.</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.length > 0 ? filteredCategories.map((elem, i) => (
                      <tr key={elem._id}>
                        <td>{(currentPage - 1) * limit + i + 1}</td>
                        <td>{elem.name}</td>
                        <td>
                          {elem.status
                            ? <span className="badge text-bg-success">True</span>
                            : <span className="badge text-bg-danger">False</span>}
                        </td>
                        <td className="text-end">
                            <div className="dropdown dropdown-action">
                                {/* eslint-disable jsx-a11y/anchor-is-valid */}
                                <a href="#" className="action-icon dropdown-toggle"
                                    data-bs-toggle="dropdown" aria-expanded="false"><i
                                        className="material-icons">more_vert</i></a>
                                <div className="dropdown-menu dropdown-menu-right">
                                    <button type='button' onClick={() => navigate('/user/manage-category', { state: elem })} className="dropdown-item"><i
                                        className="fa-solid fa-pencil m-r-5"></i> Edit</button>
                                    <button type='button' className="dropdown-item"
                                        onClick={() => handleDelete(elem?._id, elem?.name)}
                                    ><i
                                        className="fa-regular fa-trash-can m-r-5"></i> Delete</button>
                                </div>
                            </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4"><div className="no-table-data">No Data Found!</div></td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="row align-center mt-3 user-account">
                  <div className="col-md-6">
                    <div>Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, totalItems)} of {totalItems} entries</div>
                  </div>
                  <div className="col-md-6 text-end">
                    <Pagination
                      total={totalItems}
                      itemsPerPage={limit}
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
        <CopyRight />
      </div>
    </div>
  );
};

export default CategoryList;
